import { useEffect, useState, useRef } from "react";
import { Users, Send, SkipForward, Check, Trophy, LogOut, RotateCcw, Clock, Eye, EyeOff, Search, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useT } from "../lib/i18n";
import { LegalFooter } from "./LegalFooter";

const safeArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  return Object.values(val);
};

export default function GameOFA({ room, playerId, actions }) {
  const { t } = useT();
  const isLobbyHost = playerId === room?.host_id; 
  const safePlayers = safeArray(room?.players);
  const deck = safeArray(room?.deck);
  
  const [now, setNow] = useState(Date.now() / 1000);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now() / 1000), 250);
    return () => clearInterval(id);
  }, []);

  const [guess, setGuess] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  // CZAT NA ŻYWO
  const [chatMsg, setChatMsg] = useState("");
  const chatEndRef = useRef(null);
  const chatMessages = safeArray(room?.chat);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages.length]);

  const sendChatMessage = (e) => {
    e.preventDefault();
    if (!chatMsg.trim()) return;
    const newMsg = {
      id: Date.now() + Math.random(),
      senderId: playerId,
      senderName: safePlayers.find(p => p.id === playerId)?.name || "?",
      color: safePlayers.find(p => p.id === playerId)?.color || "#fff",
      text: chatMsg.trim()
    };
    const updatedChat = [...chatMessages, newMsg].slice(-50);
    actions.config({ chat: updatedChat });
    setChatMsg("");
  };

  // Zmienne fazy OFA
  const phase = room?.ofa_phase;
  const mainHostId = room?.ofa_main_host;
  const isOFAHost = safeArray(room?.ofa_hosts).includes(playerId); 
  const myCardsGuessed = room?.ofa_guessed?.[playerId] || 0;
  const targetCards = room?.cards_per_player || 2;
  const myActiveCard = room?.player_cards?.[playerId];
  
  const skipsUsed = room?.ofa_skips?.[playerId] || 0;
  const maxSkips = Math.ceil(targetCards / 2);
  const canSkip = skipsUsed < maxSkips;

  const getCardText = (card) => {
    if (!card) return "—";
    let rawLang = localStorage.getItem('language') || localStorage.getItem('lang') || 'pl';
    const currentLang = String(rawLang).split('-')[0].toLowerCase();
    if (card[currentLang]) return card[currentLang];
    if (card.en) return card.en;
    if (card.pl) return card.pl;
    if (card.text) {
       const key = String(card.text).toLowerCase();
       const translated = t(key);
       return translated !== key ? translated : card.text;
    }
    if (typeof card === 'string') return card;
    return "—";
  };

  const drawNextCard = () => {
      if (deck.length === 0) return { text: "Brak kart" };
      return deck[Math.floor(Math.random() * deck.length)];
  };

  // LOGIKA SILNIKA
  useEffect(() => {
    if (!isLobbyHost || !room) return;

    if (!phase && !room.ofa_trans_voting) {
      actions.config({ 
        ofa_trans_voting: true,
        ofa_phase: 'voting', 
        ofa_vote_start: Date.now() / 1000,
        ofa_host_votes: {},
        ofa_hosts: []
      });
      return;
    }

    if (phase === 'voting' && !room.ofa_trans_select) {
      const voteElapsed = now - (room.ofa_vote_start || now);
      const votes = room.ofa_host_votes || {};
      const allVoted = Object.keys(votes).length >= safePlayers.length;

      if (voteElapsed >= 15 || allVoted) {
        const counts = {};
        Object.values(votes).forEach(v => counts[v] = (counts[v] || 0) + 1);
        let maxVotes = 0;
        let tied = [];
        Object.entries(counts).forEach(([pId, c]) => {
            if (c > maxVotes) { maxVotes = c; tied = [pId]; }
            else if (c === maxVotes) { tied.push(pId); }
        });
        
        const winnerId = tied.length > 0 
            ? tied[Math.floor(Math.random() * tied.length)] 
            : safePlayers[Math.floor(Math.random() * safePlayers.length)].id;

        const options = {};
        safePlayers.filter(p => p.id !== winnerId).forEach(p => {
            options[p.id] = [drawNextCard(), drawNextCard(), drawNextCard()];
        });

        actions.config({
            ofa_trans_select: true,
            ofa_phase: 'card_selection',
            ofa_main_host: winnerId,
            ofa_hosts: [winnerId],
            ofa_options: options,
            ofa_selected_cards: {}
        });
      }
    }

    if (phase === 'card_selection' && !room.ofa_trans_playing) {
      const selected = room.ofa_selected_cards || {};
      const selectersCount = safePlayers.length - 1;
      
      if (Object.keys(selected).length > 0 && !room.ofa_selection_timer) {
          actions.config({ ofa_selection_timer: Date.now() / 1000 });
          return;
      }

      const timerElapsed = room.ofa_selection_timer ? now - room.ofa_selection_timer : 0;
      const allSelected = Object.keys(selected).length >= selectersCount;

      if ((room.ofa_selection_timer && timerElapsed >= 10) || allSelected) {
          const finalCards = {};
          finalCards[mainHostId] = null; 
          
          safePlayers.filter(p => p.id !== mainHostId).forEach(p => {
              if (selected[p.id]) {
                  finalCards[p.id] = selected[p.id];
              } else {
                  const opts = room.ofa_options?.[p.id] || [];
                  finalCards[p.id] = opts.length > 0 ? opts[Math.floor(Math.random() * opts.length)] : drawNextCard();
              }
          });

          const totalTime = selectersCount * targetCards * (room.turn_seconds || 60);

          actions.config({
              ofa_trans_playing: true,
              ofa_phase: 'playing',
              player_cards: finalCards,
              ofa_play_start: Date.now() / 1000,
              ofa_total_time: totalTime,
              ofa_guessed: {},
              ofa_skips: {}
          });
      }
    }

    if (phase === 'playing' && !room.ofa_ended) {
       const elapsed = now - (room.ofa_play_start || now);
       const remaining = (room.ofa_total_time || 0) - elapsed;

       const guessers = safePlayers.filter(p => !safeArray(room.ofa_hosts).includes(p.id));

       if (guessers.length === 0) {
           actions.config({ ofa_ended: true, ofa_win: true });
           actions.end(); 
       } else if (remaining <= 0) {
           actions.config({ ofa_ended: true, ofa_win: false });
           actions.end();
       }
    }
  }, [isLobbyHost, phase, now, room, safePlayers, deck, mainHostId, targetCards, actions]);

  const handleVoteHost = (targetId) => {
      actions.config({ 
          ofa_host_votes: { ...(room.ofa_host_votes || {}), [playerId]: targetId } 
      });
  };

  const handleSelectCard = (targetId, cardObj) => {
      if (playerId !== mainHostId) return;
      actions.config({
          ofa_selected_cards: { ...(room.ofa_selected_cards || {}), [targetId]: cardObj }
      });
  };

  const hostConfirmGuess = (targetPlayerId) => {
      if (isProcessing) return;
      setIsProcessing(true);
      
      const currentGuessed = room?.ofa_guessed?.[targetPlayerId] || 0;
      const newGuessedCount = currentGuessed + 1;
      
      const updates = { 
          ofa_guessed: { ...(room.ofa_guessed || {}), [targetPlayerId]: newGuessedCount }
      };

      if (newGuessedCount >= targetCards) {
          updates.ofa_hosts = [...(room.ofa_hosts || []), targetPlayerId];
          updates.player_cards = { ...(room.player_cards || {}), [targetPlayerId]: null };
          toast.success(`${safePlayers.find(p => p.id === targetPlayerId)?.name} zgadł wszystko i awansuje!`);
      } else {
          updates.player_cards = { ...(room.player_cards || {}), [targetPlayerId]: drawNextCard() };
      }
      
      actions.config(updates);
      setTimeout(() => setIsProcessing(false), 500);
  };

  const hostSkipCard = (targetPlayerId) => {
      if (isProcessing) return;
      setIsProcessing(true);

      const currentSkips = room?.ofa_skips?.[targetPlayerId] || 0;
      const updates = {
          ofa_skips: { ...(room.ofa_skips || {}), [targetPlayerId]: currentSkips + 1 },
          player_cards: { ...(room.player_cards || {}), [targetPlayerId]: drawNextCard() }
      };
      
      actions.config(updates);
      setTimeout(() => setIsProcessing(false), 500);
  };

  const handleAutoGuess = (e) => {
      e.preventDefault();
      if (!guess.trim() || !myActiveCard || isProcessing || isOFAHost) return;
      setIsProcessing(true);

      const expectedTrans = String(getCardText(myActiveCard)).toLowerCase().trim();
      const expectedOrig = String(myActiveCard?.en || myActiveCard?.pl || myActiveCard?.text || "").toLowerCase().trim();
      const g = guess.toLowerCase().trim();

      if (g === expectedTrans || g === expectedOrig) {
          toast.success(t("correct") || "Zgadłeś!");
          const newGuessedCount = myCardsGuessed + 1;
          const updates = { 
              ofa_guessed: { ...(room.ofa_guessed || {}), [playerId]: newGuessedCount }
          };

          if (newGuessedCount >= targetCards) {
              updates.ofa_hosts = [...(room.ofa_hosts || []), playerId];
              updates.player_cards = { ...(room.player_cards || {}), [playerId]: null };
          } else {
              updates.player_cards = { ...(room.player_cards || {}), [playerId]: drawNextCard() };
          }
          actions.config(updates);
      } else {
          toast.error(t("wrong") || "Nie do końca...");
      }
      setGuess("");
      setTimeout(() => setIsProcessing(false), 300);
  };

  const handleLeave = async () => {
      const updatedPlayers = safePlayers.filter(p => p.id !== playerId);
      if (actions.updateRoom) await actions.updateRoom({ ...room, players: updatedPlayers });
      if (actions.leave) actions.leave();
  };

  if (room?.status === "ended" || room?.ofa_ended) {
      const isWin = room?.ofa_win;
      return (
        <div className="grain min-h-screen flex flex-col bg-[#050510]">
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            <h1 className={`text-5xl font-black mb-4 ${isWin ? "text-[#00FF66]" : "text-[#FF007F]"}`}>
               {isWin ? "ZWYCIĘSTWO!" : "PORAŻKA!"}
            </h1>
            <p className="text-[#A0A0B0] text-lg mb-8 max-w-md">
               {isWin ? "Odgadliście wszystkie hasła! Świetna praca zespołowa!" : "Czas minął przed odgadnięciem wszystkich kart. Następnym razem pójdzie lepiej!"}
            </p>
            
            <div className="w-full max-w-md space-y-3 mb-10 text-left">
                {safePlayers.filter(p => p.id !== mainHostId).map((p) => {
                    const guessed = room?.ofa_guessed?.[p.id] || 0;
                    return (
                      <div key={p.id} className="p-4 bg-[#12121A] rounded-xl flex items-center justify-between border-2 border-white/10">
                          <div className="flex items-center gap-3">
                              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color || "#fff" }} />
                              <span className="font-bold text-white">{p.name || "?"}</span>
                          </div>
                          <div className="text-[#00FFFF] font-bold">{guessed} / {targetCards} {t("cards_word") || "kart"}</div>
                      </div>
                    );
                })}
            </div>

            {isLobbyHost && (
                <button onClick={async () => {
                    if (actions.config) await actions.config({ card_index: Math.floor(Math.random() * 1000) });
                    actions.restart();
                }} className="bg-[#FF007F] px-8 py-4 rounded-2xl font-bold flex items-center gap-2 hover:-translate-y-1 transition-transform shadow-[0_0_20px_rgba(255,0,127,0.3)]">
                    <RotateCcw size={20}/> {t("play_again") || "Zagraj ponownie"}
                </button>
            )}
          </div>
          <div className="w-full z-20 relative"><LegalFooter screen="WYNIKI" /></div>
        </div>
      );
  }

  return (
    <div className="grain relative min-h-screen flex flex-col">
      <div className="flex-1 w-full max-w-6xl mx-auto px-4 py-6 sm:p-8 relative z-10">
        
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-black text-lg">HEADS UP</span>
            <span className="text-xs font-mono2 tracking-widest px-2 py-1 rounded-full bg-[#B26DFF]/20 text-[#B26DFF] uppercase">
              {t("ofa_name") || "JEDEN ZA WSZYSTKICH"}
            </span>
          </div>
          <button onClick={handleLeave} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 border-white/15 text-[#A0A0B0] hover:border-[#FF007F] hover:text-[#FF007F] transition-colors font-bold text-xs">
             <LogOut size={16}/> {t("leave") || "Wyjdź"}
          </button>
        </div>

        {(phase === 'voting' || !phase) && (
            <div className="w-full max-w-2xl mx-auto text-center mt-10 pop-in">
                <Users className="mx-auto text-[#00FFFF] mb-4" size={50} />
                <h2 className="font-display font-black text-3xl text-white mb-2">Głosowanie na Hosta</h2>
                <p className="text-[#A0A0B0] mb-6">Wybierzcie osobę, która widzi wszystkie hasła i na start będzie naprowadzać resztę drużyny.</p>
                
                <div className="text-xl font-black text-[#FFE800] mb-6 bg-[#FFE800]/10 border-2 border-[#FFE800]/30 py-3 rounded-xl">
                   Koniec głosowania za: {Math.max(0, 15 - Math.floor(now - (room?.ofa_vote_start || now)))}s
                </div>

                <div className="grid sm:grid-cols-2 gap-4 text-left">
                    {safePlayers.map(p => {
                        const votesForThis = Object.values(room?.ofa_host_votes || {}).filter(v => v === p.id).length;
                        const iVotedForThis = room?.ofa_host_votes?.[playerId] === p.id;
                        return (
                            <button 
                               key={p.id} 
                               onClick={() => handleVoteHost(p.id)}
                               className={`p-4 rounded-xl border-2 flex items-center justify-between transition-all ${iVotedForThis ? "border-[#00FFFF] bg-[#00FFFF]/20" : "border-white/10 bg-[#12121A] hover:border-white/30"}`}
                            >
                                <div className="flex items-center gap-3">
                                   <span className="w-4 h-4 rounded-full" style={{ backgroundColor: p.color || "#fff" }} />
                                   <span className="font-bold text-white">{p.name || "?"} {p.id === playerId && "(Ty)"}</span>
                                </div>
                                {votesForThis > 0 && <span className="font-black text-[#00FFFF]">{votesForThis} {t("votes_word")}</span>}
                            </button>
                        )
                    })}
                </div>
            </div>
        )}

        {phase === 'card_selection' && (
            <div className="w-full max-w-4xl mx-auto text-center mt-10 pop-in">
                <h2 className="font-display font-black text-3xl text-white mb-2">Przydzielanie kart</h2>
                
                {playerId === mainHostId ? (
                    <>
                        <p className="text-[#A0A0B0] mb-6">Zostałeś wybrany Hostem! Wybierz początkowe karty dla każdego gracza.</p>
                        {room?.ofa_selection_timer && (
                            <div className="text-xl font-black text-[#FF007F] mb-6 bg-[#FF007F]/10 border-2 border-[#FF007F]/30 py-3 rounded-xl animate-pulse">
                                Koniec wyboru za: {Math.max(0, 10 - Math.floor(now - room.ofa_selection_timer))}s
                            </div>
                        )}
                        <div className="grid md:grid-cols-2 gap-6 text-left">
                            {safePlayers.filter(p => p.id !== mainHostId).map(p => {
                                const opts = room?.ofa_options?.[p.id] || [];
                                const selected = room?.ofa_selected_cards?.[p.id];
                                return (
                                    <div key={p.id} className="bg-[#12121A] border-2 border-white/10 rounded-2xl p-4">
                                        <div className="font-bold text-[#00FFFF] mb-3 flex items-center gap-2">
                                            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color || "#fff" }} /> {p.name}
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            {opts.map((opt, i) => (
                                                <button 
                                                    key={i} 
                                                    onClick={() => handleSelectCard(p.id, opt)}
                                                    className={`p-3 rounded-xl border-2 font-bold transition-all text-sm ${selected?.text === opt?.text ? "border-[#00FF66] bg-[#00FF66]/20 text-[#00FF66]" : "border-white/5 bg-black/50 text-[#A0A0B0] hover:text-white"}`}
                                                >
                                                    {getCardText(opt)}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center min-h-[300px]">
                        <EyeOff className="text-[#505060] mb-4" size={60}/>
                        <div className="font-display text-2xl font-black text-[#FFE800] mb-2">Host dobiera karty...</div>
                        <p className="text-[#A0A0B0] font-bold">Przygotuj się do zgadywania!</p>
                        {room?.ofa_selection_timer && (
                            <div className="mt-6 text-sm font-bold text-[#FF007F]">
                                Start za: {Math.max(0, 10 - Math.floor(now - room.ofa_selection_timer))}s
                            </div>
                        )}
                    </div>
                )}
            </div>
        )}

        {phase === 'playing' && (
            <div className="pop-in">
                <div className="flex flex-col sm:flex-row items-center justify-between p-4 sm:p-6 bg-[#12121A] border-2 border-white/10 rounded-2xl mb-6 shadow-2xl">
                    <div className="flex items-center gap-4 mb-4 sm:mb-0">
                        <div className="w-14 h-14 bg-[#B26DFF]/20 text-[#B26DFF] rounded-full flex items-center justify-center">
                            <Clock size={28} />
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-widest text-[#A0A0B0] font-mono2 mb-1">Wspólny Czas</div>
                            <div className={`font-display font-black text-3xl sm:text-4xl ${Math.max(0, room.ofa_total_time - (now - room.ofa_play_start)) <= 30 ? "text-[#FF007F] animate-pulse" : "text-white"}`}>
                                {Math.max(0, Math.ceil(room.ofa_total_time - (now - room.ofa_play_start)))}s
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="text-right">
                            <div className="text-xs uppercase tracking-widest text-[#A0A0B0] font-mono2 mb-1">Twoja Rola</div>
                            <div className={`font-bold ${isOFAHost ? "text-[#00FFFF]" : "text-[#FFE800]"}`}>
                                {isOFAHost ? "Host (Naprowadzasz)" : "Gracz (Zgadujesz)"}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6">
                    
                    <div className="p-6 sm:p-10 rounded-3xl bg-[#12121A] border-2 border-[#00FFFF]/30 flex flex-col items-center justify-center min-h-[350px] relative">
                        {isOFAHost ? (
                            <div className="w-full h-full flex flex-col">
                                <h3 className="font-display text-2xl font-black text-[#00FFFF] mb-6 text-center flex items-center justify-center gap-2">
                                    <Eye size={28} /> Aktywne hasła drużyny
                                </h3>
                                <div className="grid sm:grid-cols-2 gap-4 w-full">
                                    {safePlayers.filter(p => !safeArray(room?.ofa_hosts).includes(p.id)).map(p => {
                                        const cCard = room?.player_cards?.[p.id];
                                        const cGuessed = room?.ofa_guessed?.[p.id] || 0;
                                        const pSkips = room?.ofa_skips?.[p.id] || 0;
                                        
                                        return (
                                            <div key={p.id} className="p-4 bg-black/40 border-2 border-white/10 rounded-xl relative overflow-hidden flex flex-col justify-between">
                                                <div className="absolute top-0 left-0 w-1 h-full" style={{ backgroundColor: p.color || "#fff" }} />
                                                <div className="pl-2 mb-3">
                                                    <div className="text-xs text-[#A0A0B0] font-bold mb-1 flex justify-between">
                                                        <span>{p.name} zgaduje:</span>
                                                        <span className="text-[#00FFFF]">{cGuessed}/{targetCards}</span>
                                                    </div>
                                                    <div className="font-display font-black text-xl text-[#FFE800]">
                                                        {cCard ? getCardText(cCard) : "Losowanie..."}
                                                    </div>
                                                </div>
                                                
                                                <div className="flex gap-2 pl-2">
                                                    <button onClick={() => hostConfirmGuess(p.id)} className="flex-1 bg-[#00FF66] text-[#050510] py-2 rounded-lg font-bold flex items-center justify-center gap-1 hover:bg-[#00FF66]/80 transition-colors">
                                                        <Check size={16}/> {t("got_it") || "Zgadł!"}
                                                    </button>
                                                    <button onClick={() => hostSkipCard(p.id)} disabled={pSkips >= maxSkips} className="flex-1 bg-white/10 text-white py-2 rounded-lg font-bold flex items-center justify-center gap-1 disabled:opacity-30 hover:bg-white/20 transition-colors">
                                                        <SkipForward size={16}/> {t("skip_card") || "Pomiń"}
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center w-full flex flex-col items-center">
                                <EyeOff className="text-[#505060] mb-4" size={60}/>
                                <div className="font-display text-2xl font-black text-[#505060] mb-2">{t("hidden_ask") || "Zgaduj hasło!"}</div>
                                <div className="text-[#00FFFF] font-bold mb-6 tracking-widest uppercase text-xs">
                                   Odgadnięto: <span className="text-white text-sm">{myCardsGuessed} / {targetCards}</span>
                                </div>
                                
                                <form onSubmit={handleAutoGuess} className="w-full max-w-sm flex gap-2 mb-4">
                                   <input value={guess} onChange={(e) => setGuess(e.target.value)} placeholder={t("type_guess") || "Wpisz odpowiedź..."} className="flex-1 bg-[#12121A] border-2 border-white/10 rounded-xl py-3 px-4 focus:outline-none focus:border-[#FFE800]" />
                                   <button type="submit" className="px-5 rounded-xl bg-[#FFE800] text-[#050510] font-bold"><Send size={20}/></button>
                                </form>
                                
                                <button 
                                   onClick={() => {
                                      if (isProcessing || !canSkip || isOFAHost) return;
                                      setIsProcessing(true);
                                      actions.config({
                                          ofa_skips: { ...(room.ofa_skips || {}), [playerId]: skipsUsed + 1 },
                                          player_cards: { ...(room.player_cards || {}), [playerId]: drawNextCard() }
                                      });
                                      setTimeout(() => setIsProcessing(false), 500);
                                   }} 
                                   disabled={!canSkip}
                                   className={`w-full max-w-sm flex items-center justify-center gap-2 py-3.5 rounded-xl border-2 font-bold transition-colors ${canSkip ? "border-[#FF007F]/50 text-[#FF007F] hover:bg-[#FF007F]/10" : "border-white/10 text-white/30 bg-white/5 cursor-not-allowed"}`}
                                >
                                   <SkipForward size={18}/> {t("skip_card") || "Pomiń kartę"} ({skipsUsed}/{maxSkips})
                                </button>
                            </div>
                        )}
                    </div>
                    
                    <div className="space-y-4">
                        {/* CZAT NA ŻYWO */}
                        <div className="p-4 rounded-2xl bg-[#12121A] border-2 border-white/10 flex flex-col h-[350px]">
                            <div className="flex items-center gap-2 text-[#A0A0B0] mb-3 font-mono2 text-xs tracking-widest uppercase">
                                <MessageSquare size={16}/> Czat (Podpowiedzi)
                            </div>
                            <div className="flex-1 overflow-y-auto custom-scrollbar flex flex-col gap-2 mb-3 pr-2">
                                {chatMessages.length === 0 ? (
                                    <div className="text-[#505060] text-xs text-center my-auto">Brak wiadomości. Zacznij podpowiadać!</div>
                                ) : (
                                    chatMessages.map(msg => (
                                        <div key={msg.id} className="text-sm leading-tight bg-black/30 p-2 rounded-lg">
                                            <span className="font-bold" style={{ color: msg.color }}>{msg.senderName}: </span>
                                            <span className="text-white/90 break-words">{msg.text}</span>
                                        </div>
                                    ))
                                )}
                                <div ref={chatEndRef} />
                            </div>
                            <form onSubmit={sendChatMessage} className="flex gap-2">
                                <input 
                                    value={chatMsg} 
                                    onChange={(e) => setChatMsg(e.target.value)} 
                                    placeholder="Napisz podpowiedź..." 
                                    className="flex-1 bg-black/50 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#00FFFF] text-white" 
                                />
                                <button type="submit" disabled={!chatMsg.trim()} className="px-3 bg-[#00FFFF] text-[#050510] rounded-xl font-bold disabled:opacity-50">
                                    <Send size={16}/>
                                </button>
                            </form>
                        </div>
                    </div>

                </div>
            </div>
        )}

      </div>
      <div className="w-full z-20 relative"><LegalFooter screen="OFA" /></div>
    </div>
  );
}